import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatINR, formatDate, statusClass } from '../utils/api';
import { ClientFormModal } from './Clients';

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client,  setClient]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit,setShowEdit]= useState(false);
  const [newContact, setNewContact] = useState({ name:'', role:'', phone:'', email:'' });
  const [addingContact, setAddingContact] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/clients/${id}`).then(d => { setClient(d); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading-state">Loading…</div>;
  if (!client)  return <div className="empty-state">Client not found.</div>;

  async function handleAddContact() {
    if (!newContact.name) return;
    setAddingContact(true);
    await api.post(`/clients/${id}/contacts`, newContact);
    setNewContact({ name:'', role:'', phone:'', email:'' });
    setAddingContact(false);
    load();
  }

  async function handleDeleteContact(cid) {
    if (!window.confirm('Remove this contact?')) return;
    await api.delete(`/clients/${id}/contacts/${cid}`);
    load();
  }

  return (
    <div className="page-content">
      <div className="topbar">
        <div className="breadcrumb">
          <span style={{cursor:'pointer',color:'var(--red)'}} onClick={() => navigate('/clients')}>Clients</span>
          <span> / {client.name}</span>
        </div>
        <div className="topbar-right">
          <button className="btn btn-outline" onClick={() => setShowEdit(true)}>✎ Edit Client</button>
        </div>
      </div>

      {/* Client Info */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <div className="card card-body">
          <div style={{fontSize:16,fontWeight:700,color:'var(--text-dark)',marginBottom:10}}>{client.name}</div>
          {[['GSTIN', client.gstin],['PAN', client.pan],['Phone', client.phone],
            ['Email', client.email],['Website', client.website],['Address', client.address],
            ['City/State', [client.city, client.state, client.pincode].filter(Boolean).join(', ')],
            ['Payment Terms', `${client.payment_advance}% / ${client.payment_interim}% / ${client.payment_final}%`]
          ].map(([label, val]) => val ? (
            <div key={label} style={{display:'flex',gap:12,marginBottom:5,fontSize:12}}>
              <span style={{color:'var(--text-light)',minWidth:110}}>{label}</span>
              <span style={{color:'var(--text-dark)',fontWeight:500}}>{val}</span>
            </div>
          ) : null)}
          {client.link && <a href={client.link} target="_blank" rel="noreferrer" style={{fontSize:12,color:'var(--red)'}}>🔗 Drive link</a>}
        </div>

        {/* Contacts */}
        <div className="card">
          <div className="card-header"><span className="card-title">Contacts</span></div>
          <div style={{padding:'10px 14px',display:'flex',flexDirection:'column',gap:8}}>
            {client.contacts.map(c => (
              <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 8px',background:'var(--bg-subtle)',borderRadius:5}}>
                <div>
                  <div style={{fontWeight:600,fontSize:12,color:'var(--text-dark)'}}>{c.name} {c.role && <span style={{fontWeight:400,color:'var(--text-light)'}}>· {c.role}</span>}</div>
                  <div style={{fontSize:10,color:'var(--text-light)'}}>{[c.phone,c.email].filter(Boolean).join(' · ')}</div>
                </div>
                <button className="icon-btn" onClick={() => handleDeleteContact(c.id)}>✕</button>
              </div>
            ))}
            <div style={{display:'flex',gap:6,marginTop:4}}>
              <input className="form-input" style={{flex:2,fontSize:11}} placeholder="Name *" value={newContact.name} onChange={e=>setNewContact(n=>({...n,name:e.target.value}))} />
              <input className="form-input" style={{flex:1,fontSize:11}} placeholder="Role" value={newContact.role} onChange={e=>setNewContact(n=>({...n,role:e.target.value}))} />
            </div>
            <div style={{display:'flex',gap:6}}>
              <input className="form-input" style={{flex:1,fontSize:11}} placeholder="Phone" value={newContact.phone} onChange={e=>setNewContact(n=>({...n,phone:e.target.value}))} />
              <input className="form-input" style={{flex:1,fontSize:11}} placeholder="Email" value={newContact.email} onChange={e=>setNewContact(n=>({...n,email:e.target.value}))} />
              <button className="btn btn-primary btn-sm" onClick={handleAddContact} disabled={addingContact||!newContact.name}>Add</button>
            </div>
          </div>
        </div>
      </div>

      {/* Projects */}
      <div className="card">
        <div className="card-header"><span className="card-title">Projects</span></div>
        {client.projects.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">📁</div>No projects yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Project</th><th>Status</th><th>Start Date</th>
                <th className="num">Value</th><th className="num">Received</th><th className="num">Outstanding</th>
              </tr></thead>
              <tbody>
                {client.projects.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                    <td style={{fontWeight:600,color:'var(--text-dark)'}}>{p.name}</td>
                    <td><span className={`badge ${statusClass(p.status)}`}>{p.status}</span></td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{formatDate(p.start_date)}</td>
                    <td className="num">{formatINR(p.value)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(p.received)}</td>
                    <td className="num" style={{color:p.outstanding>0?'var(--danger)':'var(--success)',fontWeight:700}}>{formatINR(p.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEdit && <ClientFormModal client={client} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}
